import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Detail as DetailComponent, InfoHeader, useRelationshipDiscoverer } from 'src/mtska-frontend-app-component';
import { useDataProvider } from 'src/mtska-frontend-data-provider';
import { AppContext } from 'src/mtska-frontend-app-scaffolding';
import { Box, Button, ButtonGroup, CircularProgress, ListItem } from '@mui/joy';
import { Alert, Backdrop, List } from "@mui/material"

const dataProviderName = 'persons/employment';
const model = 'persons/employment';
const modelExposedName = 'Employment';
const wizardAction = 'create';

const Create = (params) => {
    const { t } = useTranslation();
    const { userCapabilities } = useContext(AppContext);

    const [drawable, setDrawable] = useState(false);
    const { doAction, item, loadOne, errorMessage, errors, saving, success } = useDataProvider(dataProviderName, params.multipart);
    const { discover } = useRelationshipDiscoverer(params, item?.data, params?.detailStack?.inverseRelation, params?.detailStack?.stack);

    const detailConfig = {
        "tabs": [
            {
                "name": "create_new_employment",
                "fieldsets": [
                    {
                        "name": "create_new_employment",
                        "fields": {
                            "poolMigrationStrategy": {
                                "type": "hidden",
                                "className": "col-12"
                            },
                            "role": {
                                "type": "text",
                                "required": "true",
                                "className": "col-12"
                            },
                            "status": {
                                "type": "select",
                                "options": [
                                    {
                                        "label": "missing_timeframe",
                                        "value": "missing_timeframe"
                                    },
                                    {
                                        "label": "terminated",
                                        "value": "terminated"
                                    },
                                    {
                                        "label": "active",
                                        "value": "active"
                                    },
                                    {
                                        "label": "future",
                                        "value": "future"
                                    }
                                ],
                                "readonly": true,
                                "className": "col-4",
                                "placeholder": "calculated_by_the_system",
                                "icon": "faChartNetwork"
                            },
                            "hired_at": {
                                "type": "date",
                                "className": "col-4",
                                "placeholder": "insert"
                            },
                            "terminated_at": {
                                "type": "date",
                                "className": "col-4",
                                "placeholder": "insert"
                            },
                            "person": {
                                "type": "relationshipSingle",
                                "relation": {
                                    "drawerRoute": "persons",
                                    "inverseRelation": "employment",
                                    "inverseRelationType": "relationshipSingle",
                                },
                                "required": true,
                                "view": "person"
                            },
                            "legalEntity": {
                                "type": "relationshipSingle",
                                "relation": {
                                    "drawerRoute": "legalentities",
                                    "inverseRelation": "person",
                                    "inverseRelationType": "relationship",
                                    // "filter": {
                                    //     "items": [{
                                    //         "field": "type",
                                    //         "operator": "equals",
                                    //         "value": "customer",
                                    //     }]
                                    // },
                                    // "limit": 1000
                                },
                                "view": "legalEntity",
                                "required": "true"
                            }
                        }
                    }
                ]
            }
        ],
        "validation": []
    }

    useEffect(() => {
        loadOne(null).then(e => setDrawable(true));
    }, []);

    const [editing, setEditing] = useState(false)

    useEffect(() => {
        if (success && editing) {
            (async () => {
                try {
                    await params.handleClose(params.uid, item.data)
                } catch (e) { }
            })()
        }
    }, [success])

    const handleSave = async (data) => {
        doAction && doAction({ action: wizardAction, model: model ? model : dataProviderName, data: data });
    }


    const [poolMigrationStrategy, setPoolMigrationStrategy] = useState('stop');
    const getCurrentEmploymentWarning = () => {
        const person = discover('person');
        if (person?.employment) {
            const alertExisting = <Alert className="alert" severity='error' sx={{ marginTop: '10px', borderRadius: '10px', boxShadow: '0 0 15px rgba(0,0,0,0.1)' }}>
                {t('{{firstname}} is yet "{{role}}" at "{{company}}". You are going to terminate the existing role.', { firstname: person.firstname, role: person.employment.role, company: person.employment.legalEntity?.name })}
            </Alert>

            let alertPool;
            if (person?.employment.pools?.length > 0) {
                alertPool = <Alert className="alert" severity='info' sx={{ marginTop: '10px', borderRadius: '10px', boxShadow: '0 0 15px rgba(0,0,0,0.1)' }}>
                    {t('As "{{role}}" at "{{company}}", {{firstname}} has assigned the following vehicles', { firstname: person.firstname, role: person.employment.role, company: person.employment.legalEntity?.name })}
                    <List>
                        {
                            person?.employment.pools.map((pool) => <ListItem>{pool.car?.plate}</ListItem>)
                        }
                    </List>
                    {t('What do you want to do?')}
                    <PoolStrategyToggle 
                        value={poolMigrationStrategy}
                        onChange={setPoolMigrationStrategy}
                        variant="solid"
                        t={t}
                    />
                </Alert>
            }

            return (
                <Box>
                    {alertExisting}
                    {alertPool}
                </Box>
            )
        }
        return null;
    }

    return (
        !drawable ? <Backdrop open={!drawable} style={{ backgroundColor: "white" }}><CircularProgress /></Backdrop> :
            <>
                <DetailComponent
                    disableDetailConfigOverride={true}
                    values={{
                        ...item?.data,
                        hired_at: item?.data?.hired_at ?? new Date(),
                        legalEntity: discover('legalEntity') ?? discover('car.legalEntity') ?? discover('employment.legalEntity'),
                        person: discover('person'),
                        poolMigrationStrategy: poolMigrationStrategy
                    }}
                    modelName={model.split('/')[1]}
                    detailConfig={detailConfig}
                    dataProviderName={dataProviderName}
                    onEditing={setEditing}
                    save={handleSave}
                    saving={saving}
                    errors={errors}
                    errorMessage={errorMessage}
                    title={t('New {{itemname}}', { itemname: t(modelExposedName) })}
                    canSave={userCapabilities && userCapabilities[dataProviderName + '.create']}
                    header={
                        <InfoHeader
                            view="wizard"
                            title={t('Create new {{itemname}}', { itemname: t(modelExposedName) })}
                            subtitle={t('Fill the basic information to create a new {{itemname}} that you will be able to edit via the standard interface', { itemname: t(modelExposedName) })}
                            actions={getCurrentEmploymentWarning()}
                        />
                    }
                    {...params}
                />
            </>
    )
}

export default Create;

const PoolStrategyToggle = ({
    value,
    onChange,
    t = (s) => s, // fallback se non usi i18n
    variant = 'solid', // o 'outlined' etc.
}) => {
    return (
        <ButtonGroup sx={{marginTop:'20px'}}>
            <Button
                variant={variant}
                color={value === 'stop' ? 'danger' : 'neutral'}
                onClick={() => onChange('stop')}
            >
                {t('Stop current assignment')}
            </Button>
            <Button
                variant={variant}
                color={value === 'migrate' ? 'success' : 'neutral'}
                onClick={() => onChange('migrate')}
            >
                {t('Migrate current assignment')}
            </Button>
        </ButtonGroup>
    );
};